<template>
  <div>
    <h4 class="member-details-form-section-header">Veteran card information</h4>
    <caption class="required-field-indicator">* Indicates required field</caption>
    <FormField v-if="isReadOnly">
      <Alert class="no-bottom-margin" :message="readOnlyVeteranCardAlert" alertType="info" />
    </FormField>
    <div class="form-section">
      <FormField labelText="Do you hold a Veteran Card?" :helperText="refData.veteranCardHelpText.informationText">
        <ul role="radiogroup" class="radio-group large">
          <li v-for="option in cardOptions" :key="`veteran-card-declaration-${option.returnValue}`">
            <input
              type="radio"
              v-model.lazy="currentActiveCard.hasCard"
              :id="`veteran-card-declaration-${option.returnValue}`"
              :value="option.returnValue"
              name="hasCard"
              :disabled="isReadOnly"
            />
            <label :for="`veteran-card-declaration-${option.returnValue}`">{{ option.display }}</label>
          </li>
        </ul>
      </FormField>
    </div>
    <FormField v-if="currentActiveCard.hasCard" :required="currentActiveCard.hasCard" labelText="Veteran Card type" class="form-section">
      <ul class="radio-group large form-section">
        <li v-for="option in refData.veteranCardTypeOptions" :key="option.returnValue" :id="removeWhitespaceFromString(option.returnValue)">
          <input type="radio" :id="option.returnValue" :value="option.returnValue" name="veteranCard" v-model.lazy="currentActiveCard.cardType" :disabled="isReadOnly" />
          <label :for="option.returnValue">{{ option.display }}</label>
        </li>
      </ul>
      <p
        id="veteran-gold-card-text"
        v-if="currentActiveCard.cardType && currentActiveCard.cardType.toLowerCase() === VeteranCardTypeOptions.VETERAN_GOLD_CARD"
        v-html="refData.veteranCardGoldInformationText.informationText"
      />
      <template v-if="currentActiveCard.cardType && currentActiveCard.cardType.toLowerCase() === VeteranCardTypeOptions.VETERAN_WHITE_CARD">
        <p id="veteran-white-card-text" class="form-section" v-html="refData.veteranCardWhiteInformationText.informationText" />
        <MultiSelect
          name="white card options"
          :id="`veteran-card-condition-options`"
          :items="refData.veteranCardWhiteOptions"
          @updateData="handleAcceptedConditionsChanged"
          :selectedItems="selectedConditions"
          placeholderText="Search for a listed accepted condition"
          ref="refData.veteranCardWhiteOptions"
          :maximum="isReadOnly ? 0 : refData.veteranCardWhiteOptions.length"
          :disabledSelectedItems="isReadOnly"
        ></MultiSelect>
      </template>
    </FormField>
  </div>
</template>

<script>
import isEqual from 'lodash.isequal';
import cloneDeep from 'lodash.clonedeep';
import MemberDetailsMixin from '@/views/MemberDetails/MemberDetailsFormSteps/MemberDetailsMixin';
import { UpdateMemberDetailsStepNames, VeteranCardTypeOptions, EligibilityTypes, OperationType } from '@/enums';
import { removeWhitespaceFromString, getCurrentDate } from '@general/js/utils';
import MultiSelect from '@general/js/components/multiSelect.vue';

export default {
  name: UpdateMemberDetailsStepNames.step6,
  mixins: [MemberDetailsMixin],

  props: {
    initialData: {
      type: Object,
    },

    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    MultiSelect,
  },

  data() {
    return {
      cardOptions: [
        { display: 'Yes', returnValue: true },
        { display: 'No', returnValue: false },
      ],
      initialActiveCard: {
        // First active card (if any) as determined from server data
        cardType: '',
        hasCard: null,
        cardIndex: null,
        cardDetails: {},
      },
      currentActiveCard: {
        cardType: '', // keeping this to make easier to access
        hasCard: null,
        cardIndex: null,
        cardDetails: {},
      },
      readOnlyVeteranCardAlert:
        'If you would like to make changes to your details below, please <a href="tel:1800335425">contact us on 1800 335 425</a>. Our friendly team will be happy to help you.',

      // Imports
      VeteranCardTypeOptions,
    };
  },

  // validations() {
  //   const obj = {
  //     currentActiveCard: {},
  //     memberDetails: {
  //       veteranCard: {
  //         hasVeteranCard: {
  //           required: requiredIf(function () {
  //             return !this.isReadOnly;
  //           }),
  //         },
  //         cardList: {},
  //       },
  //     },
  //   };

  //   if (this.currentActiveCard.hasCard) {
  //     obj.memberDetails.veteranCard.cardList = {
  //       $each: {
  //         cardType: {
  //           required: requiredIf(function () {
  //             return !this.isReadOnly;
  //           }),
  //         },
  //       },
  //     };

  //     obj.currentActiveCard.cardType = {
  //       required: requiredIf(function () {
  //         return !this.isReadOnly;
  //       }),
  //     };

  //     // if card type is white. NOTE: check if is gold or white card depend on card type contains string "gold" (case insensitive)
  //     if (RegExp(/white/i).test(this.currentActiveCard.cardType)) {
  //       // if is "white", add additional validation rule
  //       obj.currentActiveCard.cardDetails = {
  //         acceptedConditions: {
  //           conditionSelected(arr) {
  //             return arr?.length > 0;
  //           },
  //         },
  //       };
  //     }
  //   }

  //   return obj;
  // },

  computed: {
    currentlyHasCard() {
      return this.currentActiveCard.hasCard === true;
    },

    /**
     * create key-value pairs of "veteranCardWhiteOptions" from refData for "acceptedConditions" list in "MultiSelect" component
     * e.g. veteranCardWhiteOptions: {
     *        DVAC0: "Shoulder",
     *        DVAC1: "Ankle",
     *        DVAC2: "Alcohol Dependence & Abuse",
     *        ...
     *      }
     * This will enable easy look-up of a "display" value based on a given "return" value.
     */
    acceptedConditionsLookupData() {
      if (this.refData?.veteranCardWhiteOptions) {
        return this.refData.veteranCardWhiteOptions.reduce((obj, item) => {
          const pairValues = ((obj[item.returnValue] = item.display), obj);
          return pairValues;
        }, {});
      }
      return {};
    },

    /**
     * Creates an array of selected accepted condition objects in the same format as the reference data from the server.
     */
    selectedConditions() {
      if (this.currentActiveCard.cardDetails && this.currentActiveCard.cardDetails.acceptedConditions) {
        const filteredActiveAcceptedConditions = this.filterActiveAcceptedConditions(this.currentActiveCard.cardDetails);

        return filteredActiveAcceptedConditions.map((condition) => {
          return {
            display: this.acceptedConditionsLookupData[condition.code],
            returnValue: condition.code,
          };
        });
      }
      return [];
    },
  },

  mounted() {
    let firstActiveCardIndex = this.findFirstActiveVeteranCardIndex(this.initialData?.veteranCard?.veteranCardList);
    let firstActiveCard = {};

    if (firstActiveCardIndex > -1) {
      firstActiveCard = cloneDeep(this.initialData?.veteranCard.veteranCardList[firstActiveCardIndex]);
      // Clean-up the active card object by removing any end-dated accepted conditions.
      firstActiveCard.acceptedConditions = this.filterActiveAcceptedConditions(firstActiveCard);
    } else {
      firstActiveCardIndex = null; // Change this from -1 to `null` for consistency with other areas of the code.
    }

    // Keep a reference to the initially displayed card (NOTE: Treat this object as immutable).
    this.initialActiveCard = {
      hasCard: this.initialData?.veteranCard.hasVeteranCard,
      cardIndex: firstActiveCardIndex,
      cardType: firstActiveCard.cardType,
      cardDetails: firstActiveCard,
    };

    // Keep a reference to the currently displayed card (NOTE: Treat this object as mutable).
    // Initialise it with the details of the initially active card.
    this.currentActiveCard = {
      hasCard: this.memberDetails?.veteranCard.hasVeteranCard,
      cardIndex: this.initialActiveCard.cardIndex,
      cardType: this.initialActiveCard.cardType,
      cardDetails: cloneDeep(this.initialActiveCard.cardDetails),
    };
  },

  watch: {
    'memberDetails.veteranCard': {
      handler(newObj) {
        if (isEqual(newObj, this.initialData.veteranCard)) {
          this.currentActiveCard = cloneDeep(this.initialActiveCard);
        }
      },
      deep: true,
    },

    'memberDetails.eligibility.eligibilityType': {
      handler(newType, oldType) {
        if (Object.keys(this.currentActiveCard.cardDetails)?.length > 0) {
          let vuexActiveCardObj = this.memberDetails.veteranCard.veteranCardList[this.currentActiveCard.cardIndex];
          if (
            [...EligibilityTypes.SERVING, ...EligibilityTypes.EX_SERVING].includes(oldType) &&
            [...EligibilityTypes.WORK_CONNECTION, ...EligibilityTypes.RELATED].includes(newType)
          ) {
            const newEndDate = getCurrentDate();

            this.currentActiveCard.cardDetails.endDate = newEndDate;
            this.currentActiveCard.cardDetails.op = OperationType.REMOVE;
            vuexActiveCardObj.endDate = newEndDate;
            vuexActiveCardObj.op = OperationType.REMOVE;

            // revert back acceptance conditions to initial acceptance conditions that came back from server
            this.currentActiveCard.cardDetails.acceptedConditions = cloneDeep(this.initialActiveCard.cardDetails.acceptedConditions) || [];

            // update all acceptance conditions end date to be today
            this.currentActiveCard.cardDetails.acceptedConditions.forEach((condition, index) => {
              condition.endDate = newEndDate;
              condition.op = OperationType.REMOVE;
              vuexActiveCardObj.acceptedConditions[index].endDate = newEndDate;
              vuexActiveCardObj.acceptedConditions[index].op = OperationType.REMOVE;
            });

            vuexActiveCardObj.acceptedConditions = cloneDeep(this.currentActiveCard.cardDetails.acceptedConditions);
          } else if (this.currentActiveCard.cardDetails.endDate !== this.initialActiveCard.cardDetails.endDate) {
            const initialCardDetail = this.initialActiveCard.cardDetails;
            this.currentActiveCard.cardDetails = cloneDeep(initialCardDetail);
            vuexActiveCardObj = cloneDeep(initialCardDetail);
          }
        }
      },
    },

    'currentActiveCard.hasCard': {
      handler(bool) {
        /**
         * Handler for when the "Do you hold a Veteran Card?" Yes/No button is selected.
         *
         * These are the rules applied:
         * Scenario 1 - The new declaration value is the same as the old declaration value, then the details should reflect the currently active card details.
         * This scenario can be achieved if during the session but prior to submit, the user had (1) changed the declaration value from A to B then back to A, or
         * (2) clicked on the already selected value.
         * Scenario 2 - The declaration value changed from A to B, i.e. null/N to Y, null/Y to N.
         */
        if (bool !== undefined) {
          const newVeteranCardDetails = cloneDeep(this.initialData.veteranCard);
          const initialActiveCard = this.initialActiveCard;
          const currentActiveCard = this.currentActiveCard;

          // Scenario 1:
          if (initialActiveCard.hasCard === bool) {
            if (initialActiveCard.hasCard !== bool && currentActiveCard.cardIndex === null) {
              currentActiveCard.cardIndex = initialActiveCard.cardIndex;
              currentActiveCard.cardType = initialActiveCard.cardType;
              currentActiveCard.cardDetails = cloneDeep(initialActiveCard.cardDetails);
            }
          }

          // Scenario 2:
          else {
            currentActiveCard.cardIndex = null;
            currentActiveCard.cardType = '';
            currentActiveCard.cardDetails = {};

            newVeteranCardDetails.hasVeteranCard = bool;
            // If N, then end-date and exiting cards and their accepted conditions.
            if (bool === false) {
              newVeteranCardDetails.veteranCardList = this.endDateActiveVeteranCards(newVeteranCardDetails.veteranCardList);
            }
          }

          // Update the Veteran Card details that is stored in Vuex.
          this.updateMemberDetails(cloneDeep({ ...this.memberDetails, veteranCard: newVeteranCardDetails }));
        }
      },
    },

    'currentActiveCard.cardType': {
      handler(cardType) {
        /**
         * Handler for when the Veteran Card Type is changed.
         *
         * These are the rules applied:
         * Scenario 1 - The new Veteran Card Type is the same as the old Veteran Card Type, then the card details should reflect what was returned by the server.
         * This scenario can be achieved if during the session but prior to submit, the user had changed the Veteran Card Type from A to B then back to A.
         * Scenario 2 - The Veteran Card Type changed from A to B, or vice versa.
         */
        if (cardType) {
          const newVeteranCardDetails = cloneDeep({ ...this.initialData.veteranCard, hasVeteranCard: this.currentActiveCard.hasCard });
          const newMemberDetails = cloneDeep(this.memberDetails);
          const initialActiveCard = this.initialActiveCard;
          const currentActiveCard = this.currentActiveCard;

          // Scenario 1:
          if (initialActiveCard.cardType && initialActiveCard.cardType.toLowerCase() === cardType.toLowerCase()) {
            newMemberDetails.veteranCard = newVeteranCardDetails;

            currentActiveCard.cardIndex = initialActiveCard.cardIndex;
            currentActiveCard.cardType = initialActiveCard.cardType;
            currentActiveCard.cardDetails = cloneDeep(initialActiveCard.cardDetails);
          }

          // Scenario 2:
          else {
            // Before adding the new card, end-date the previously active cards and associated accepted conditions, otherwise will end-date the new card too.
            newVeteranCardDetails.veteranCardList = this.endDateActiveVeteranCards(newVeteranCardDetails.veteranCardList);

            const newCard = {
              op: OperationType.ADD,
              id: null, // New cards that have not yet been submitted to OHI have no ID.
              cardType,
              acceptedConditions: [], // Not applicable for Veteran Gold Card. For White Card, it should be initially set to [] here.
              startDate: getCurrentDate(), // Always set to today's date.
              endDate: null, // Not applicable since this is a new/active card.
            };

            // Add the new card to the existing list of cards.
            newVeteranCardDetails.veteranCardList.push(newCard);

            // Set the currently active card to this new card.
            currentActiveCard.cardIndex = newVeteranCardDetails.veteranCardList.length - 1;
            currentActiveCard.cardType = cardType;
            currentActiveCard.cardDetails = newCard;
          }

          // Update the Veteran Card details that is stored in Vuex.
          this.updateMemberDetails(cloneDeep({ ...this.memberDetails, veteranCard: newVeteranCardDetails }));
        }
      },
    },
  },

  methods: {
    handleAcceptedConditionsChanged(selection) {
      const existingAcceptedConditions = cloneDeep(this.initialActiveCard.cardDetails.acceptedConditions);

      // If there are NO accepted conditions selected:
      if (selection.length === 0 && existingAcceptedConditions) {
        // Copy the old card details back to the `currentActiveCard` object.
        this.currentActiveCard.cardDetails.acceptedConditions = cloneDeep(existingAcceptedConditions);
        // End-date the accepted condition(s) since there are currently no more selections.
        this.currentActiveCard.cardDetails.acceptedConditions.forEach((condition) => {
          condition.op = OperationType.REMOVE;
          condition.endDate = getCurrentDate();
        });
      }
      // If there are accepted conditions selected:
      else {
        const acceptedConditions = [];

        if (existingAcceptedConditions) {
          // Check if one or more initial accepted conditions have been de-selected.
          const removedExistingConditions = existingAcceptedConditions.filter((initialCondition) => {
            return !selection.some((condition) => {
              return initialCondition.code === condition.returnValue;
            });
          });

          // For any initial accepted conditions that have been de-selected, re-add it to the list of latest accepted conditions but end-date it.
          if (removedExistingConditions.length > 0) {
            removedExistingConditions.forEach((condition) => {
              acceptedConditions.push({
                op: OperationType.REMOVE,
                id: condition.id,
                code: condition.code,
                startDate: condition.startDate,
                endDate: getCurrentDate(),
              });
            });
          }
        }

        // Otherwise, for each selection, convert into a proper accepted conditions object data structure.
        selection.forEach((condition) => {
          let indexOfExistingCondition = -1; // Default

          if (existingAcceptedConditions) {
            // Check if the current condition is actually an existing condition.
            indexOfExistingCondition = existingAcceptedConditions.findIndex((existingCondition) => {
              return existingCondition.code === condition.returnValue;
            });

            // If it is an existing condition, re-add it to the list of latest accepted conditions using the initial details.
            if (indexOfExistingCondition > -1) {
              acceptedConditions.push({
                op: OperationType.NONE,
                id: existingAcceptedConditions[indexOfExistingCondition].id,
                code: existingAcceptedConditions[indexOfExistingCondition].code,
                startDate: existingAcceptedConditions[indexOfExistingCondition].startDate,
                endDate: existingAcceptedConditions[indexOfExistingCondition].endDate,
              });
            }
          }

          // Else, treat this as an entirely new accepted condition.
          if (indexOfExistingCondition === -1) {
            acceptedConditions.push({
              op: OperationType.ADD,
              id: null, // New accepted conditions that have not yet been submitted to OHI have no ID.
              code: condition.returnValue,
              startDate: getCurrentDate(), // Always set to today's date.
              endDate: '',
            });
          }
        });

        this.currentActiveCard.cardDetails.acceptedConditions = acceptedConditions;
      }

      const newMemberDetails = cloneDeep(this.memberDetails);
      newMemberDetails.veteranCard.veteranCardList[this.currentActiveCard.cardIndex] = this.currentActiveCard.cardDetails;

      this.updateMemberDetails(newMemberDetails);
    },

    removeWhitespaceFromString,
  },
};
</script>

<style lang="scss" scoped>
.form-section {
  margin-bottom: 25px;

  p .form-section {
    margin-bottom: 0;
  }
}

.form-field {
  :deep(.item3.helper-text) {
    @include tablet-and-above {
      p {
        margin-bottom: 0;
      }
    }
  }
}

#veteran-white-card-text {
  margin-bottom: 15px;
}

.selected-items-wrapper {
  padding-bottom: 0.2rem;

  button {
    color: white;
    background-color: $GREY_800;
    border-radius: 17.5px;
    line-height: rem(19px);
    max-width: max-content;
    min-height: auto;
    padding: 10px 16px;
    position: relative;
    box-shadow: none;
    text-align: left;
    margin: 0px 10px 8px 0px;
  }
}
</style>
